import axios from 'axios';
import { mockApi, isDemoSession } from './mockData';

const resolveApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';

  return isLocal ? 'http://localhost:5000/api/v1' : `${window.location.origin}/api/v1`;
};

export const API_URL = resolveApiUrl();
export const APP_BASE_URL = API_URL.replace(/\/api\/v1\/?$/, '');

// Criar instância do axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isDemoSession()) {
      // Token expirado ou inválido
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Serviços de Auth
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => isDemoSession() ? mockApi.updateProfile(data) : api.put('/auth/profile', data),
  updatePassword: (data) => isDemoSession() ? mockApi.updatePassword(data) : api.put('/auth/password', data),
};

// Serviços de Veículos
export const vehicleService = {
  getAll: () => isDemoSession() ? mockApi.getVehicles() : api.get('/vehicles'),
  getById: (id) => api.get(`/vehicles/${id}`),
  create: (data) => isDemoSession() ? mockApi.createVehicle(data) : api.post('/vehicles', data),
  update: (id, data) => isDemoSession() ? mockApi.updateVehicle(id, data) : api.put(`/vehicles/${id}`, data),
  delete: (id) => isDemoSession() ? mockApi.deleteVehicle(id) : api.delete(`/vehicles/${id}`),
  toggleBlock: (id) => isDemoSession() ? mockApi.toggleVehicleBlock(id) : api.put(`/vehicles/${id}/block`),
  getPosition: (id) => api.get(`/vehicles/${id}/position`),
};

// Serviços de Posições
export const positionService = {
  getHistory: (vehicleId, params) => isDemoSession() ? mockApi.getPositions(vehicleId, params) : api.get(`/positions/${vehicleId}`, { params }),
  getRoute: (vehicleId, params) => isDemoSession() ? mockApi.getRoute(vehicleId, params) : api.get(`/positions/${vehicleId}/route`, { params }),
  reverseGeocode: (params) => api.get('/positions/reverse-geocode', { params }),
  create: (data) => api.post('/positions', data),
  mobile: (data) => isDemoSession() ? mockApi.simulatePosition(data) : api.post('/positions/mobile', data),
  simulate: (data) => isDemoSession() ? mockApi.simulatePosition(data) : api.post('/positions/simulate', data),
};

// Serviços de Alertas
export const alertService = {
  getAll: (params) => isDemoSession() ? mockApi.getAlerts(params) : api.get('/alerts', { params }),
  getById: (id) => api.get(`/alerts/${id}`),
  create: (data) => api.post('/alerts', data),
  getSettings: () => isDemoSession() ? mockApi.getAlertSettings() : api.get('/alerts/settings'),
  updateSettings: (vehicleId, data) => isDemoSession() ? mockApi.updateAlertSettings(vehicleId, data) : api.put(`/alerts/settings/${vehicleId}`, data),
  markAsRead: (id) => isDemoSession() ? mockApi.markAlertAsRead(id) : api.put(`/alerts/${id}/read`),
  markAllAsRead: () => isDemoSession() ? mockApi.markAllAlertsAsRead() : api.put('/alerts/read-all'),
  delete: (id) => isDemoSession() ? mockApi.deleteAlert(id) : api.delete(`/alerts/${id}`),
  getUnreadCount: () => isDemoSession() ? mockApi.getUnreadCount() : api.get('/alerts/unread/count'),
};

export const systemService = {
  getHealth: () => isDemoSession()
    ? Promise.resolve({
      data: {
        success: true,
        message: 'RCrastro API esta funcionando em modo demonstracao',
        timestamp: new Date().toISOString(),
      },
    })
    : axios.get(`${APP_BASE_URL}/health`),
};

export const userService = {
  getAll: () => isDemoSession() ? mockApi.getUsers() : api.get('/users'),
  create: (data) => isDemoSession() ? mockApi.createUser(data) : api.post('/users', data),
  update: (id, data) => isDemoSession() ? mockApi.updateUser(id, data) : api.put(`/users/${id}`, data),
  delete: (id) => isDemoSession() ? mockApi.deleteUser(id) : api.delete(`/users/${id}`),
};

export const geofenceService = {
  getAll: () => isDemoSession() ? mockApi.getGeofences() : api.get('/geofences'),
  getById: (id) => isDemoSession() ? mockApi.getGeofences().then((response) => ({
    data: {
      ...response.data,
      data: response.data.data.find((geofence) => geofence.id === id),
    },
  })) : api.get(`/geofences/${id}`),
  create: (data) => isDemoSession() ? mockApi.createGeofence(data) : api.post('/geofences', data),
  update: (id, data) => isDemoSession() ? mockApi.updateGeofence(id, data) : api.put(`/geofences/${id}`, data),
  delete: (id) => isDemoSession() ? mockApi.deleteGeofence(id) : api.delete(`/geofences/${id}`),
};

export const clientService = {
  getAll: () => isDemoSession() ? mockApi.getClients() : api.get('/clients'),
  getById: (id) => isDemoSession() ? mockApi.getClients().then((response) => ({
    data: {
      ...response.data,
      data: response.data.data.find((client) => client.id === id),
    },
  })) : api.get(`/clients/${id}`),
  create: (data) => isDemoSession() ? mockApi.createClient(data) : api.post('/clients', data),
  update: (id, data) => isDemoSession() ? mockApi.updateClient(id, data) : api.put(`/clients/${id}`, data),
  delete: (id) => isDemoSession() ? mockApi.deleteClient(id) : api.delete(`/clients/${id}`),
};

export const invoiceService = {
  getAll: () => isDemoSession() ? mockApi.getInvoices() : api.get('/invoices'),
  getById: (id) => isDemoSession() ? mockApi.getInvoices().then((response) => ({
    data: {
      ...response.data,
      data: response.data.data.find((invoice) => invoice.id === id),
    },
  })) : api.get(`/invoices/${id}`),
  create: (data) => isDemoSession() ? mockApi.createInvoice(data) : api.post('/invoices', data),
  update: (id, data) => isDemoSession() ? mockApi.updateInvoice(id, data) : api.put(`/invoices/${id}`, data),
  markPaid: (id, data) => isDemoSession() ? mockApi.markInvoicePaid(id, data) : api.put(`/invoices/${id}/pay`, data),
  delete: (id) => isDemoSession() ? mockApi.deleteInvoice(id) : api.delete(`/invoices/${id}`),
};

export const commandService = {
  getAll: () => isDemoSession() ? mockApi.getCommands() : api.get('/commands'),
  create: (data) => isDemoSession() ? mockApi.createCommand(data) : api.post('/commands', data),
  updateStatus: (id, data) => isDemoSession() ? mockApi.updateCommandStatus(id, data) : api.put(`/commands/${id}/status`, data),
  delete: (id) => isDemoSession() ? mockApi.deleteCommand(id) : api.delete(`/commands/${id}`),
};

export const deviceService = {
  getAll: () => isDemoSession() ? mockApi.getDevices() : api.get('/devices'),
  create: (data) => isDemoSession() ? mockApi.createDevice(data) : api.post('/devices', data),
  update: (id, data) => isDemoSession() ? mockApi.updateDevice(id, data) : api.put(`/devices/${id}`, data),
  delete: (id) => isDemoSession() ? mockApi.deleteDevice(id) : api.delete(`/devices/${id}`),
};

export default api;
